import React, { useEffect, useContext, useState, useRef }  from 'react'
import { View, StyleSheet, TouchableOpacity, ScrollView, KeyboardAvoidingView, FlatList, Platform } from 'react-native'
import { Icon, Button, Text, Input, Spinner, RadioGroup, Radio } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import { UserContext } from '../../global/UserProvider'
import { deleteTransfer, saveNewTransfer } from '../../global/Firebase/firebase'
import * as Sentry from "@sentry/react-native"
import { findProductByBarcode } from '../../utils/barcodeScan'
import ProductSearch from '../../components/ProductSearch'
import { useTranslation } from 'react-i18next'
import { useAtomValue, atom } from 'jotai'
import { productsDictAtom, productsByBarcodeDictAtom, storesDictAtom, productUnitsDictAtom, currencyFormatterAtom, userRoleAtom } from '../../global/AtomsProvider'
import { getDailyStatID } from '../../utils/timezoneFunctions'
import HeaderIconButton from '../../components/HeaderIconButton'
import AdaptiveTableCard from '../../components/AdaptiveTableCard'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'
import { saveFailureText } from '../../global/Firebase/writeOutcome'

export default function TransferDetailScreen( { navigation, route } ) {
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const db = context.db
  const { detail, itemIndex, sectionIndex, addItemToData, editItemInData, removeItemFromData, preselectedStore } = route.params 
  const productsByBarcode = useAtomValue(productsByBarcodeDictAtom)
  const productUnitsDict = useAtomValue(productUnitsDictAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const productsDict = useAtomValue(productsDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const [items, setItems] = useState(detail ? (detail.items || [{...detail.product, quantity: detail.quantity}]) : []) // support old format where there was only one product in tansfer
  const [fromStore, setFromStore] = useState(detail ? (detail.fromStore?.id ? detail.fromStore : storesDict?.[detail.fromStore]) : (preselectedStore ? storesDict?.[preselectedStore] : null))
  const [toStore, setToStore] = useState(detail ? (detail.toStore?.id ? detail.toStore : storesDict?.[detail.toStore]) : null)
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const _barcodeInput = useRef()

  const handleBarcodeInput = ({nativeEvent}) => {
    nativeEvent?.preventDefault?.()
    let [product, weight] = findProductByBarcode(nativeEvent.text, productsByBarcode)
    if (product) {
      addItem(product, weight)
    }
    _barcodeInput.current.clear()
  }
  

  // Log transfer detail data for E2E testing
  useEffect(() => {
    if (detail != null) {
      console.log('[TRANSFER_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: detail.id,
        // Scalar-map: a full store object carries a live native Firestore ref (cyclical on iOS),
        // so stringifying it raw is the fatal crash (Sentry 26A/26B). e2e contract: { id, name } | null.
        fromStore: detail.fromStore?.id
          ? { id: detail.fromStore.id, name: detail.fromStore.name }
          : { id: detail.fromStore, name: storesDict?.[detail.fromStore]?.name || detail.fromStore },
        toStore: detail.toStore?.id
          ? { id: detail.toStore.id, name: detail.toStore.name }
          : { id: detail.toStore, name: storesDict?.[detail.toStore]?.name || detail.toStore },
        organizationId: context.user?.organizationId,
        cashierUID: detail.cashierUID,
        amount: detail.amount,
        items: (detail.items || []).map(item => ({
          id: item.id,
          name: item.name || item.text,
          quantity: item.quantity,
          purchasePrice: item.purchasePrice,
        })),
      })))
    }
  }, [detail])

  useEffect(() => {
    if (userRole?.permissions?.deleteTransfer || context.user.role === 'admin') {
      navigation.setOptions({
        headerRight: () => (
          detail != null && <HeaderIconButton
            onPress={async () => {
              if (!isLoading) {
                try {
                  setIsLoading(true)
                  await deleteTransfer(context.db, detail)
                  removeItemFromData(sectionIndex, itemIndex)
                  setIsLoading(false)
                  navigation.goBack()
                } catch(error) {
                  console.log(error)
                  setSaveError(t('error'))
                  Sentry.captureException(error, { tags: { books_write_error: 'transfer-delete' } })
                }
              }
            }}
            iconName={isLoading ? 'loader-outline' : 'trash-outline'}
            testID='detail-delete-button'
          />
        ),
      })
    }
  }, [isLoading])

  const addItem = (item, quantity=1) => {
    // if item present increase quantity
    setItems(items => {
      var newArr = [...items]
      var itemIndex = newArr.findIndex(x => x.id == item.id)
      if (itemIndex > -1) {
        let oldItem = newArr[itemIndex]
        let newItem = {...oldItem, quantity: parseFloat(oldItem.quantity) + quantity}
        newArr[itemIndex] = newItem
      } else {
        let newItem = {
          ...item, 
          quantity,
        }
        newArr.push(newItem)
      }
      return newArr
    })
  }

  const setItemQuantity = (index, value) => {
    setItems(items => {
      let newArr = [...items]
      let oldItem = newArr[index]
      let newItem = {...oldItem, quantity: value}
      newArr[index] = newItem
      return newArr
    })
  }

  const totalSum = (items) => {
    return items.reduce((total, item) => total + ((item.purchasePrice || 0) * item.quantity), 0)
  }

  const save = async() => {
    if (detail == null) {
      setIsLoading(true)
      if (items.length > 0 && fromStore && toStore && fromStore?.id !== toStore?.id) {
        setSaveError('')
        let updatedItems = []
        for (let item of items) {
          let updatedItem = {...item, quantity: parseFloat(item.quantity)}
          updatedItems.push(updatedItem)
        }
        const info = {
          items: updatedItems,
          fromStore,
          toStore,
          amount: totalSum(items),
          date: new Date(),
          fromDailyStatID: getDailyStatID(fromStore.id, new Date()),
          toDailyStatID: getDailyStatID(toStore.id, new Date()),
          cashierUID: context.user.uid,
          organizationId: context.user.organizationId
        }
        try {
          let ref = await saveNewTransfer(db, info)
          addItemToData({...info, ref, id: ref.id}, info.date, info.fromStore)
          console.log('[TRANSFER_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
            id: ref.id,
            // Scalar-map (see useEffect above): info.fromStore/toStore are full store objects with a
            // live ref; stringifying raw throws AFTER a successful save → catch shows a false error.
            fromStore: info.fromStore ? { id: info.fromStore.id, name: info.fromStore.name } : null,
            toStore: info.toStore ? { id: info.toStore.id, name: info.toStore.name } : null,
            organizationId: context.user?.organizationId,
            cashierUID: info.cashierUID,
            amount: info.amount,
            items: (info.items || []).map(item => ({
              id: item.id,
              name: item.name || item.text,
              quantity: item.quantity,
              purchasePrice: item.purchasePrice,
            })),
          })));
          setSuccessMessageVisible(true)
          setIsLoading(false)
        } catch (error) {
          Sentry.captureException(error, { tags: { books_write_error: 'transfer-create' } })
          console.log(error)
          setSaveError(saveFailureText(error, t, t('error')))
          setIsLoading(false)
        }
      } else {
        setSaveError(t('notAllFieldsFilled'))
        setIsLoading(false)
      }
    }
  }

  const getStockForStore = (product, storeId) => {
    if (!storeId) return ''
    // Items loaded from a saved transfer (e.g. opened from the operations list
    // under Supabase mode) carry only {name, quantity, productId} and lack the
    // full product's storeStock map. Fall back to the live productsDict entry,
    // then guard the lookup so a missing map can't crash the detail screen.
    const stockSource = product.storeStock
      ? product
      : (productsDict?.[product.id] || productsDict?.[product.productId])
    return stockSource?.storeStock?.[storeId] || 0
  }

  const fields = [
    {
      key: 'name',
      type: 'text',
      columnStyle: 'nameColumn',
      title: t('transfers.name'),
      textCategory: 's1'
    },
    {
      key: 'fromStoreStock',
      type: 'text',
      title: t('transfers.inStockFromStore'),
      formatter: (value, item) => getStockForStore(item, fromStore?.id)
    },  
    {
      key: 'toStoreStock',
      type: 'text',
      title: t('transfers.inStockToStore'),
      formatter: (value, item) => getStockForStore(item, toStore?.id)
    },
    {
      key: 'quantity',
      type: 'quantity',
      title: t('transfers.quantity'),
      caption: item => (productUnitsDict[item.productUnit]?.shortName || t('pcs')),
      onChange: (index, value) => setItemQuantity(index, value)
    }
  ]

  const renderFooter = () => (
    <Text category='label' style={styles.totalSum}>
      {t('transfers.total') + ': ' + currencyFormatter.format(totalSum(items))}
    </Text>
  )

  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset={100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        <Text category='label' style={styles.label}>
          {t('transfers.fromStore')}
        </Text>
        { Object.values(storesDict).map((item, index) => (
          <View
            key={item.id}
            testID={`transfer-from-store-${index}`}
            {...(Platform.OS === 'web' ? { dataSet: { testid: `transfer-from-store-${index}`, storeid: item.id } } : {})}
          >
            <Radio style={styles.radio} checked={fromStore?.id === item.id} onChange={() => {setFromStore(Object.values(storesDict)[index])}}>
              {item.name}
            </Radio>
          </View>
        ))}

        <Text category='label' style={styles.label}>
          {t('transfers.toStore')}
        </Text>
        { Object.values(storesDict).map((item, index) => (
          <View
            key={item.id}
            testID={`transfer-to-store-${index}`}
            {...(Platform.OS === 'web' ? { dataSet: { testid: `transfer-to-store-${index}`, storeid: item.id } } : {})}
          >
            <Radio style={styles.radio} checked={toStore?.id === item.id} onChange={() => {setToStore(Object.values(storesDict)[index])}}>
              {item.name}
            </Radio>
          </View>
        ))}

        <Text category='label' style={styles.label}>
          {t('transfers.products')}
        </Text>
        { detail == null && (
          <View style={{flexDirection: 'row', marginBottom: 10, flexWrap: 'wrap', gap: 10}}>
            <ProductSearch 
              onSelectCallback={addItem}
              store={'all'}
            />
            <Input 
              key={'barcode'}
              ref={_barcodeInput}
              placeholder={t('transfers.inputBarcode')}
              autoCapitalize="none"
              blurOnSubmit={false}
              selectTextOnFocus={true}
              accessoryLeft={(props) => (<Icon name='grid-outline' {...props} style={{...props.style, marginRight: 0}} />)}
              autoCorrect={false}
              returnKeyType='done'
              onSubmitEditing={handleBarcodeInput}
              style={{minWidth: 200, flexGrow: 1}}
            />
          </View>
        )}
        <View style={styles.tableContainer}>
          <AdaptiveTableCard
            data={items}
            fields={fields}
            disabled={detail != null}
            breakpoint={700}
            renderFooter={renderFooter}
            ListEmptyComponent={<Text appearance='hint' style={styles.emptyMessage}>{t('transfers.addThroughBarcodeHint')}</Text>}
          />
        </View>
       
        
        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        { successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
            {t('back')}
          </Button>
          </>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          !successMessageVisible && (
          <Button onPress={save} style={styles.submitButton} disabled={detail != null}>
            {t('save')}
          </Button>
          )
        )}
        <View style={{height: 500}}></View>
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },

  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  submitButton: {
    marginTop: 16,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  radio: {
    marginBottom: 5,
  },

  totalSum: {
    marginTop: 10
  },
  tableContainer: {
    minHeight: 100,
  },
  emptyMessage: {
    flex: 1,
    alignSelf: 'center',
    textAlign: 'center',
    marginVertical: 20,
  },
})